import "./style.scss";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { TabContext, TabPanel } from "@mui/lab";
import { CircularProgress, Drawer } from "@mui/material";
import ActionSend from "assets/img/svg/actionSend";
import Downloadnew from "assets/img/svg/downloadnew";
import SendEmailIcon from "assets/img/svg/sendEmail";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import { sendAdvancePaymentMailWithStatement, setLastSentAtAdvance } from "store/advancePaymentSlice";
import { fetchAdvancePaymentsStatement, fetchAndSaveAdvanceStatement } from "store/statementsStore";
import type { AppDispatch, RootState } from "store/store";
import { useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import ButtonBase from "../ButtonBase";
import EmptyState from "../EmptyState";
import SendEmail from "../SendEmail";
import { Spinner } from "../Spinner";
import { formatLocaleDateString, getStatementFilename } from "./../../../helpers/utils";

const statementType = [
    { labelKey: "Summary.StatementOfGrossDuties", type: "gross" },
    { labelKey: "Summary.StatementOfNetDuties", type: "net" },
    { labelKey: "Summary.PrepaymentCertification", type: "precount" },
];

interface IShowAdvanceStatements {
    toggleDrawer: (anchor: any, open: any) => any;
    isOpen: boolean;
    slice: IAdvanceSlice | null;
}

const ShowAdvanceStatements = ({ toggleDrawer, isOpen, slice }: IShowAdvanceStatements) => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const [currentTab, setCurrentTab] = useState(1);
    const { status, advancePaymentNetStatement, advancePaymentPrecountStatement, savingStatus } = useSelector(
        (state: RootState) => state.statements,
    );
    const { advanceSlices } = useAppSelector((state) => state.advancePayment);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const hasAuthorPortalAccess = slice?.accountStatus && slice?.accountStatus == "confirmed";
    const sendTermText = hasAuthorPortalAccess ? t("Summary.SendToBeneficiarPortal") : t("Summary.SendToBeneficiar");
    // store gets updated normally but the component doesn't refreshes so had to put locally the optimistic design
    const tempLastSeenAt = advanceSlices.find((item: any) => item.id == slice?.id)?.lastSentAt;
    const lastSeenAt = slice?.lastSentAt || tempLastSeenAt || null;

    useEffect(() => {
        if (!slice) return;
        if (slice.precount != 1) setCurrentTab(1);
        if (isOpen) {
            currentTab == 1 &&
                !advancePaymentNetStatement &&
                dispatch(
                    fetchAdvancePaymentsStatement({
                        termId: slice.termId,
                        advancePaymentId: slice.paymentId,
                        advancePaymentSliceId: slice.id,
                        statementType: "net",
                    }),
                );
            currentTab == 2 &&
                !advancePaymentPrecountStatement &&
                dispatch(
                    fetchAdvancePaymentsStatement({
                        termId: slice.termId,
                        advancePaymentId: slice.paymentId,
                        advancePaymentSliceId: slice.id,
                        statementType: "precount",
                    }),
                );
        }
    }, [slice, isOpen, currentTab]);

    const savePdf = useCallback(() => {
        if (savingStatus == "pending" || !slice) return;
        dispatch(
            fetchAndSaveAdvanceStatement({
                termId: slice.termId,
                advancePaymentId: slice.paymentId,
                advancePaymentSliceId: slice.id,
                fileName: getStatementFilename(
                    slice.author,
                    slice.name,
                    t(statementType[currentTab].labelKey),
                    new Date(),
                ),
                statementType: statementType[currentTab].type,
            }),
        );
    }, [dispatch, savingStatus, currentTab, slice]);

    const sendEmail = (emails: string[], subject: string, body: string) => {
        if (!slice) return;
        dispatch(
            sendAdvancePaymentMailWithStatement({
                termId: slice.termId,
                advancePaymentId: slice.paymentId,
                advancePaymentSliceId: slice.id,
                mailCc: emails,
                subject,
                body,
            }),
        );
        dispatch(setLastSentAtAdvance({ advanceId: slice.paymentId, sliceId: slice.id }));
        setIsModalOpen(false);
    };

    return (
        <div>
            <Drawer anchor={"right"} open={isOpen} onClose={toggleDrawer("right", false)}>
                <SendEmail
                    isModalOpen={isModalOpen}
                    termData={{
                        author: slice?.author ?? "",
                        title: slice?.title ?? "",
                        email: slice?.email,
                        precount: slice?.precount ? slice?.precount.toString() : "0",
                        beneficiaryId: slice?.beneficiaryId,
                    }}
                    showGross={false}
                    onClose={() => setIsModalOpen(false)}
                    sendEmail={sendEmail}
                    hasAuthorPortalAccess={hasAuthorPortalAccess}
                />
                <div className="show-advance-statements">
                    <div className="show-advance-statements-header">
                        <div className="show-advance-statements-goBack" onClick={toggleDrawer("right", false)}>
                            <KeyboardArrowLeftIcon />
                            {t("ShowStatements.GoBack")}
                        </div>
                        <div className="show-advance-statements-current-statement">{t("ShowStatements.Statement")}</div>
                        <div className="show-advance-statements-header-actions">
                            {/* <div className="show-advance-statements-header-button"><Printer /></div> */}
                            <div className="show-advance-statements-header-button" onClick={() => savePdf()}>
                                {(savingStatus == "pending" && (
                                    <CircularProgress size={20} sx={{ color: variables["primary-purple"] }} />
                                )) || <Downloadnew />}
                            </div>
                            <div className="show-advance-statements-header-buttonBase">
                                <ButtonBase
                                    styles={{ borderRadius: "5px", gap: "8px" }}
                                    variant="contained"
                                    disabled={!slice?.email || (!!hasAuthorPortalAccess && !!lastSeenAt)}
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    {slice?.email
                                        ? hasAuthorPortalAccess && lastSeenAt
                                            ? t("SendEmail.AlreadyTransmit")
                                            : sendTermText
                                        : t("Summary.NoEmail")}
                                    {hasAuthorPortalAccess ? <ActionSend /> : <SendEmailIcon />}
                                </ButtonBase>
                            </div>
                        </div>
                    </div>
                    <div className="show-advance-statements-main">
                        <TabContext value={currentTab.toString()}>
                            <div className="show-advance-statement-top">
                                <div className="show-advance-statements-buttons-list">
                                    {/* <div className={`show-advance-statements-button${currentTab == 0 && " active" || ""}`} onClick={() => setCurrentTab(0)}>{t("ShowStatements.GrossDuties")}</div> */}
                                    <div
                                        className={`show-advance-statements-button${
                                            (currentTab == 1 && " active") || ""
                                        }`}
                                        onClick={() => setCurrentTab(1)}
                                    >
                                        {t("ShowStatements.NetDuties")}
                                    </div>
                                    <div
                                        className={`show-advance-statements-button${
                                            (currentTab == 2 && " active") || ""
                                        }${!slice?.precount ? " disabled" : ""}`}
                                        onClick={() => slice?.precount && setCurrentTab(2)}
                                    >
                                        {t("ShowStatements.Prepayment")}
                                    </div>
                                </div>
                                {lastSeenAt && (
                                    <div className="show-advance-statement-lastSentAt">
                                        {hasAuthorPortalAccess
                                            ? t("Management.SentToPortalAt")
                                            : t("Management.MailSentAt")}{" "}
                                        {formatLocaleDateString({
                                            date: lastSeenAt,
                                            options: { numeric: true, timeZone: "UTC" },
                                        })}
                                        <div className="badge"></div>
                                    </div>
                                )}
                            </div>
                            <TabPanel sx={{ padding: 0 }} value="0"></TabPanel>
                            <TabPanel value="1" sx={{ padding: 0 }}>
                                {status === "pending" && (
                                    <div className="spinner-wrapper">
                                        <Spinner />
                                        <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                    </div>
                                )}
                                {status === "failed" && (
                                    <div className="spinner-wrapper">
                                        <EmptyState type="statementFailed" />
                                    </div>
                                )}
                                {status === "succeeded" && (
                                    <div
                                        style={{
                                            maxWidth: "960px",
                                            border: "39px solid #c8c8c852",
                                            paddingBottom: 24,
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: advancePaymentNetStatement,
                                        }}
                                    ></div>
                                )}
                            </TabPanel>
                            <TabPanel value="2" sx={{ padding: 0 }}>
                                {status === "pending" && (
                                    <div className="spinner-wrapper">
                                        <Spinner />
                                        <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                    </div>
                                )}
                                {status === "failed" && (
                                    <div className="spinner-wrapper">
                                        <EmptyState type="statementFailed" />
                                    </div>
                                )}
                                {status === "succeeded" && (
                                    <div
                                        style={{
                                            maxWidth: "960px",
                                            border: "39px solid #c8c8c852",
                                            paddingBottom: 24,
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: advancePaymentPrecountStatement,
                                        }}
                                    ></div>
                                )}
                            </TabPanel>
                        </TabContext>
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default ShowAdvanceStatements;
